import { useContext, useState, useMemo, useCallback } from "react";
import { AlquileresContext } from "../../../../context/AlquileresContext";
import { saveIndice, deleteIndice } from "../../../../application/indices";
import { fetchIPCHistorial, fetchICLHistorial } from "../../../../application/arglyApi";
import { FaPlusCircle, FaTrash, FaInfoCircle, FaSync, FaCheck } from "react-icons/fa";
import { useSortableData } from "../../../../hooks/useSortableData";
import SortableHeader from "../../../../components/SortableHeader";

const MESES = [
  "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
];

const TIPOS = [
  { value: "IPC", label: "IPC (Indice de Precios al Consumidor)", source: "INDEC" },
  { value: "ICL", label: "ICL (Indice Contratos Locacion)", source: "BCRA" },
  { value: "CASA_PROPIA", label: "Casa Propia / Index", source: "Banco Hipotecario" },
];

function Indices({ embedded = false }) {
  const { indices, refreshIndices } = useContext(AlquileresContext);
  const now = new Date();
  const [tipo, setTipo] = useState("IPC");
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [form, setForm] = useState({
    mes: now.getMonth() + 1,
    anio: now.getFullYear(),
    valor: "",
  });

  const filteredIndices = useMemo(() => {
    return indices.filter((i) => i.tipo === tipo);
  }, [indices, tipo]);

  const getValue = useCallback(
    (i, key) => (key === "periodo" ? i.anio * 100 + i.mes : i?.[key]),
    []
  );
  const { sortedData: sortedIndices, sortConfig, requestSort } = useSortableData(filteredIndices, {
    getValue,
    storageKey: "sort-indices",
    initial: { key: "periodo", direction: "desc" },
  });

  const acumuladoAnual = useMemo(() => {
    const anioActual = new Date().getFullYear();
    const delAnio = indices
      .filter((i) => i.tipo === tipo && i.anio === anioActual)
      .sort((a, b) => a.mes - b.mes);
    if (delAnio.length === 0) return 0;

    if (tipo === "ICL") {
      // ICL is an absolute index value: accumulated change = (last / first) - 1
      const first = Number(delAnio[0].valor);
      const last = Number(delAnio[delAnio.length - 1].valor);
      if (!first) return 0;
      return ((last / first - 1) * 100).toFixed(1);
    }

    // IPC / CASA_PROPIA: monthly percentages, compounded
    let factor = 1;
    delAnio.forEach((i) => {
      factor *= 1 + Number(i.valor) / 100;
    });
    return ((factor - 1) * 100).toFixed(1);
  }, [indices, tipo]);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.valor) return;
    setSaving(true);
    try {
      await saveIndice({
        tipo,
        mes: Number(form.mes),
        anio: Number(form.anio),
        valor: Number(form.valor),
      });
      await refreshIndices();
      setForm({ ...form, valor: "" });
    } catch (error) {
      console.error("Error al guardar indice:", error);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("¿Eliminar este indice?")) return;
    try {
      await deleteIndice(id);
      await refreshIndices();
    } catch (error) {
      console.error("Error:", error);
    }
  };

  // Sync IPC from Argly API
  const handleSyncIPC = async () => {
    setSyncing(true);
    setSyncResult(null);
    try {
      const historial = await fetchIPCHistorial();
      let count = 0;
      for (const entry of historial) {
        // Check if we already have this month
        const exists = indices.find(
          (i) => i.tipo === "IPC" && i.mes === entry.mes && i.anio === entry.anio
        );
        if (!exists) {
          await saveIndice({
            tipo: "IPC",
            mes: entry.mes,
            anio: entry.anio,
            valor: Number(entry.valor),
          });
          count++;
        }
      }
      await refreshIndices();
      setSyncResult({ success: true, message: `IPC sincronizado: ${count} meses nuevos importados (${historial.length} totales disponibles).` });
    } catch (error) {
      console.error("Error al sincronizar IPC:", error);
      setSyncResult({ success: false, message: `Error al sincronizar: ${error.message}` });
    } finally {
      setSyncing(false);
    }
  };

  // Sync ICL history from Argly. ICL is published daily by BCRA, but for
  // contracts we only need one value per month -- we keep the latest entry
  // available within each month.
  const handleSyncICL = async () => {
    setSyncing(true);
    setSyncResult(null);
    try {
      const historial = await fetchICLHistorial();
      if (!historial.length) {
        setSyncResult({ success: false, message: "Argly no devolvio datos de ICL" });
        return;
      }

      // Aggregate to monthly: latest valor seen for each (mes, anio)
      const byMonth = new Map();
      for (const entry of historial) {
        if (!entry.fecha) continue;
        const [d, m, y] = String(entry.fecha).split("/");
        const day = Number(d), mes = Number(m), anio = Number(y);
        if (!mes || !anio) continue;
        const key = `${anio}-${mes}`;
        const valor = Number(entry.valor);
        if (!Number.isFinite(valor)) continue;
        const stamp = new Date(anio, mes - 1, day || 1).getTime();
        const prev = byMonth.get(key);
        if (!prev || stamp > prev.stamp) {
          byMonth.set(key, { mes, anio, valor, stamp });
        }
      }

      let count = 0;
      for (const entry of byMonth.values()) {
        const exists = indices.find(
          (i) => i.tipo === "ICL" && i.mes === entry.mes && i.anio === entry.anio
        );
        if (!exists) {
          await saveIndice({
            tipo: "ICL",
            mes: entry.mes,
            anio: entry.anio,
            valor: entry.valor,
          });
          count++;
        }
      }
      await refreshIndices();
      setSyncResult({
        success: true,
        message: `ICL sincronizado: ${count} meses nuevos importados (${byMonth.size} totales disponibles).`,
      });
    } catch (error) {
      console.error("Error al sincronizar ICL:", error);
      setSyncResult({ success: false, message: `Error: ${error.message}` });
    } finally {
      setSyncing(false);
    }
  };

  const inputClass =
    "border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-100 focus:ring-2 focus:ring-primary focus:border-transparent";

  return (
    <div className={embedded ? "space-y-6" : "px-4 md:px-6 py-4 space-y-6 max-w-4xl mx-auto"}>
      {!embedded && (
        <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100">
          Indices de Actualizacion
        </h1>
      )}

      {/* Info */}
      <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 flex items-start gap-3">
        <FaInfoCircle className="text-blue-500 mt-0.5 flex-shrink-0" />
        <div className="text-sm text-blue-700 dark:text-blue-400">
          <p>Podes cargar los indices manualmente o <strong>sincronizar automaticamente</strong> desde la API de Argly (datos oficiales de INDEC/BCRA).</p>
          <p className="mt-1">Los valores de IPC son el porcentaje mensual. El ICL es un valor absoluto (el ajuste se calcula como ICL_nuevo / ICL_viejo).</p>
        </div>
      </div>

      {/* Tipo selector */}
      <div className="flex gap-2">
        {TIPOS.map((t) => (
          <button
            key={t.value}
            onClick={() => { setTipo(t.value); setSyncResult(null); }}
            className={`px-4 py-2 text-sm rounded-lg transition-colors ${
              tipo === t.value
                ? "bg-primary text-white"
                : "bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600"
            }`}
          >
            {t.value}
          </button>
        ))}
      </div>

      {/* Acumulado + Sync */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {TIPOS.find((t) => t.value === tipo)?.label}
          </p>
          <p className="text-xs text-gray-400">Fuente: {TIPOS.find((t) => t.value === tipo)?.source}</p>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-right">
            <p className="text-2xl font-bold text-primary">{acumuladoAnual}%</p>
            <p className="text-xs text-gray-400">Acumulado {now.getFullYear()}</p>
          </div>
          {tipo === "IPC" && (
            <button
              onClick={handleSyncIPC}
              disabled={syncing}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-green-500 hover:bg-green-600 text-white rounded-lg transition-colors disabled:opacity-50"
            >
              <FaSync className={`w-3.5 h-3.5 ${syncing ? "animate-spin" : ""}`} />
              {syncing ? "Sincronizando..." : "Sync Argly"}
            </button>
          )}
          {tipo === "ICL" && (
            <button
              onClick={handleSyncICL}
              disabled={syncing}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-green-500 hover:bg-green-600 text-white rounded-lg transition-colors disabled:opacity-50"
            >
              <FaSync className={`w-3.5 h-3.5 ${syncing ? "animate-spin" : ""}`} />
              {syncing ? "Sincronizando..." : "Sync Argly"}
            </button>
          )}
        </div>
      </div>

      {/* Sync result */}
      {syncResult && (
        <div className={`rounded-lg p-3 flex items-center gap-2 text-sm ${
          syncResult.success
            ? "bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400"
            : "bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400"
        }`}>
          {syncResult.success ? <FaCheck /> : <FaInfoCircle />}
          {syncResult.message}
        </div>
      )}

      {/* Add form */}
      <form onSubmit={handleSave} className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-5">
        <h2 className="text-base font-semibold text-gray-800 dark:text-gray-100 mb-4">
          Cargar Indice Manual
        </h2>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Mes</label>
            <select
              value={form.mes}
              onChange={(e) => setForm({ ...form, mes: e.target.value })}
              className={inputClass}
            >
              {MESES.map((m, i) => (
                <option key={i} value={i + 1}>{m}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Año</label>
            <select
              value={form.anio}
              onChange={(e) => setForm({ ...form, anio: e.target.value })}
              className={inputClass}
            >
              {[2023, 2024, 2025, 2026, 2027].map((a) => (
                <option key={a} value={a}>{a}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
              {tipo === "ICL" ? "Valor (indice)" : "Valor (%)"}
            </label>
            <input
              type="number"
              value={form.valor}
              onChange={(e) => setForm({ ...form, valor: e.target.value })}
              className={`${inputClass} w-32`}
              placeholder={tipo === "ICL" ? "32.4" : "3.5"}
              step="0.01"
              min="0"
              required
            />
          </div>
          <button
            type="submit"
            disabled={saving}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-primary hover:bg-secondary text-white rounded-lg transition-colors disabled:opacity-50"
          >
            <FaPlusCircle className="w-3.5 h-3.5" />
            {saving ? "Guardando..." : "Guardar"}
          </button>
        </div>
      </form>

      {/* Table */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm overflow-hidden">
        <div className="px-5 py-3 border-b border-gray-100 dark:border-gray-700">
          <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-100">
            Historial {tipo} ({filteredIndices.length} registros)
          </h3>
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
              <SortableHeader sortKey="periodo" currentKey={sortConfig.key} direction={sortConfig.direction} onSort={requestSort} align="left" className="px-4 py-3 font-semibold text-gray-600 dark:text-gray-400">Periodo</SortableHeader>
              <SortableHeader sortKey="valor" currentKey={sortConfig.key} direction={sortConfig.direction} onSort={requestSort} align="right" className="px-4 py-3 font-semibold text-gray-600 dark:text-gray-400">
                {tipo === "ICL" ? "Valor (indice)" : "Valor (%)"}
              </SortableHeader>
              <th className="text-center px-4 py-3 font-semibold text-gray-600 dark:text-gray-400 w-16"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
            {sortedIndices.length === 0 ? (
              <tr>
                <td colSpan={3} className="px-4 py-8 text-center text-gray-400">
                  No hay indices cargados para {tipo}
                  {(tipo === "IPC" || tipo === "ICL") && (
                    <span className="block mt-1">
                      Usa el boton "Sync Argly" para importar automaticamente
                    </span>
                  )}
                </td>
              </tr>
            ) : (
              sortedIndices.map((idx) => (
                <tr key={idx.idFirebase} className="hover:bg-gray-50 dark:hover:bg-gray-700/30">
                  <td className="px-4 py-3 text-gray-800 dark:text-gray-200">
                    {MESES[idx.mes - 1]} {idx.anio}
                  </td>
                  <td className="px-4 py-3 text-right font-medium text-gray-800 dark:text-gray-200">
                    {tipo === "ICL" ? idx.valor : `${idx.valor}%`}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <button
                      onClick={() => handleDelete(idx.idFirebase)}
                      className="text-gray-400 hover:text-red-500 transition-colors"
                    >
                      <FaTrash className="w-3.5 h-3.5" />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* API credit */}
      <p className="text-xs text-gray-400 text-center">
        Datos provistos por Argly API (api.argly.com.ar) - Fuentes oficiales: INDEC, BCRA
      </p>
    </div>
  );
}

export default Indices;
